import { useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { initialDepositFilters, type DepositFilters } from './DepositToolbar';
import { LeadDepositRanking } from './LeadDepositRanking';
import { MemberDepositRanking } from './MemberDepositRanking';
import './ranking.css';

/** Por quem o depositado é classificado. Na URL (`?aba=depositos&por=cliente`), para dar para mandar o link. */
type By = 'membro' | 'cliente';
const BY_TABS: { value: By; label: string }[] = [
  { value: 'membro', label: 'Por membro do comercial' },
  { value: 'cliente', label: 'Por cliente' },
];

/**
 * O ranking de depósitos — a contabilização do leilão (PAI-1). Por padrão, os
 * membros do comercial pelo depositado dos clientes deles; em "Por cliente",
 * os clientes que mais depositaram. As duas visões dividem o recorte, e abrir
 * um membro leva aos clientes dele. Só administradores (pages/RankingPage.tsx).
 */
export function DepositRanking() {
  const [params, setParams] = useSearchParams();
  // `?aba=clientes` é o link de antes das duas visões: abre os clientes.
  const by: By = params.get('por') === 'cliente' || params.get('aba') === 'clientes' ? 'cliente' : 'membro';
  const [filters, setFilters] = useState<DepositFilters>(initialDepositFilters);
  const change = (patch: Partial<DepositFilters>) => setFilters((current) => ({ ...current, ...patch }));

  /** Voltar aos membros solta o membro aberto: "Por cliente" pela aba volta a ser de todos. */
  function choose(next: By) {
    if (next === 'membro') change({ memberId: '' });
    setParams(next === 'cliente' ? { aba: 'depositos', por: 'cliente' } : { aba: 'depositos' }, { replace: true });
  }

  /** O botão clicado some com a troca de visão: o foco vai para a aba que abriu. */
  function openMember(memberId: string) {
    change({ memberId });
    choose('cliente');
    document.getElementById('deposit-tab-cliente')?.focus();
  }

  /** Setas trocam de aba, como pede o padrão de abas. */
  function onTabKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = by === 'membro' ? 'cliente' : 'membro';
    choose(next);
    document.getElementById(`deposit-tab-${next}`)?.focus();
  }

  return (
    <>
      <div className="view-switch deposit-by" role="tablist" aria-label="Classificar os depósitos" onKeyDown={onTabKey}>
        {BY_TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`deposit-tab-${item.value}`}
            aria-selected={by === item.value}
            aria-controls="deposit-panel"
            tabIndex={by === item.value ? 0 : -1}
            onClick={() => choose(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div id="deposit-panel" role="tabpanel" aria-labelledby={`deposit-tab-${by}`}>
        {by === 'cliente' ? (
          <LeadDepositRanking filters={filters} onChange={change} />
        ) : (
          <MemberDepositRanking filters={filters} onChange={change} onOpenMember={openMember} />
        )}
      </div>
    </>
  );
}
